import type { Experiment } from '../types/vuser';

export interface ProgressMetrics {
  completed: number;
  total: number;
  failed: number;
  percent: number;
  failedPercent: number;
  /** Optional caption, e.g. dialog counts while bar tracks messages */
  subLabel?: string;
}

export function computeProgressMetrics(
  completed: number,
  total: number,
  failed = 0
): Omit<ProgressMetrics, 'subLabel'> {
  const safeTotal = Math.max(0, total);
  const safeCompleted = Math.max(0, completed);
  const safeFailed = Math.max(0, failed);
  const percent =
    safeTotal > 0 ? Math.min(100, Math.max(0, (safeCompleted / safeTotal) * 100)) : 0;
  const failedPercent =
    safeTotal > 0 ? Math.min(100 - percent, (safeFailed / safeTotal) * 100) : 0;
  return {
    completed: safeCompleted,
    total: safeTotal,
    failed: safeFailed,
    percent,
    failedPercent,
  };
}

/** Prefer message-level progress; fall back to dialog counts for older experiments. */
export function getExperimentProgressMetrics(experiment: Experiment): ProgressMetrics {
  const totalMessages = experiment.total_messages ?? 0;
  const completedMessages = experiment.completed_messages ?? 0;
  const failedMessages = experiment.failed_messages ?? 0;

  if (totalMessages > 0) {
    const metrics = computeProgressMetrics(completedMessages, totalMessages, failedMessages);
    const dialogLabel =
      typeof experiment.completed_dialogs === 'number' &&
      typeof experiment.total_dialogs === 'number'
        ? `${experiment.completed_dialogs} / ${experiment.total_dialogs} dialogs`
        : undefined;
    return { ...metrics, subLabel: dialogLabel };
  }

  const metrics = computeProgressMetrics(
    experiment.completed_dialogs ?? 0,
    experiment.total_dialogs ?? 0,
    experiment.failed_dialogs ?? 0
  );
  return metrics;
}
